import { v5 as uuidv5 } from "uuid";
import {
  ContentType,
  type MarketRecommendation,
  type WebContentPayload,
} from "@repo/types/recommendation";
import type { IMarketEventDTO } from "@repo/types/prediction-event";
import { debugLog, errorLog } from "../config";

const pollMaxAttempts = 7;
const pollBaseMs = 500;
const pollBackoffFactor = 2;
const pollJitter = 0.2;
// Cap per-attempt delay so the cumulative budget stays near the backend's
// shared 30s outbound-HTTP timeout floor. Without the cap, attempt-5's raw
// delay would be 16000ms; clamping to 10000ms keeps the worst-case sum
// (~30.6s with +20% jitter) within the same order as the backend tolerance.
const pollMaxDelayMs = 10_000;

// Exponential backoff with ±20% jitter so concurrent clients don't synchronize
// their retries on a struggling backend. Capped exponential (no jitter): 500,
// 1000, 2000, 4000, 8000, 10000 ms; total max wait ≈ 25.5s, ≈ 30.6s
// worst-case with jitter ceiling.
function computePollDelay(attempt: number): number {
  const raw = pollBaseMs * pollBackoffFactor ** attempt;
  const capped = Math.min(raw, pollMaxDelayMs);
  const jitterMultiplier = 1 + (Math.random() * 2 - 1) * pollJitter;
  return capped * jitterMultiplier;
}

/**
 * Same namespace as `@repo/utils/content/content-id.ts`.
 * Duplicated here because the widget is a standalone IIFE browser bundle.
 */
const contentNamespace = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";

export function generateWebContentId(url: string): string {
  return uuidv5(url, contentNamespace);
}

// --- HTTP envelope types (widget-only — wrap canonical DTOs from @repo/types) ---

interface ApiResponse<T> {
  data: T;
  message?: string;
}

interface PaginatedApiResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface IngestionResponse {
  commandId: string;
}

export interface CommandStatusResponse {
  commandId: string;
  status: string;
  data?: { contentId: string; matchedMarketEventIds: string[] };
  error?: string;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError";
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  constructor(baseUrl: string, apiKey: string, tenantId: string) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.headers = {
      "x-api-key": apiKey,
      "x-tenant-id": tenantId,
      "Content-Type": "application/json",
    };
  }

  async ingestContent(
    content: WebContentPayload,
  ): Promise<IngestionResponse | null> {
    try {
      const res = await fetch(
        `${this.baseUrl}/widget/recommendation/ingestion`,
        {
          method: "POST",
          headers: this.headers,
          body: JSON.stringify({ type: ContentType.WEB, content }),
        },
      );

      if (!res.ok) {
        errorLog(`Ingestion failed with status ${res.status}`);
        return null;
      }

      const json = (await res.json()) as ApiResponse<IngestionResponse>;
      return json.data;
    } catch (err) {
      errorLog("Ingestion request failed", err);
      return null;
    }
  }

  async checkRecommendations(
    contentId: string,
    limit = 1,
  ): Promise<MarketRecommendation[] | null> {
    try {
      const url = new URL(`${this.baseUrl}/widget/recommendation/markets`);
      url.searchParams.set("contentId", contentId);
      url.searchParams.set("limit", String(limit));

      const res = await fetch(url.toString(), { headers: this.headers });

      // 204 = the server confirmed there are no recommendations for this
      // contentId yet (typically a first visit — embedding not in Qdrant).
      // This is the normal cold-path trigger, not an error condition, so
      // skip JSON parsing and return cleanly without logging.
      if (res.status === 204) {
        debugLog(
          "No recommendations yet for contentId — proceeding to cold path",
        );
        return null;
      }

      if (res.ok) {
        const json = (await res.json()) as ApiResponse<MarketRecommendation[]>;

        if (json.data && json.data.length > 0) {
          debugLog("Existing recommendations found (fast path)");
          return json.data;
        }
      }

      return null;
    } catch (err) {
      errorLog("Check recommendations failed", err);
      return null;
    }
  }

  /* eslint-disable no-await-in-loop -- Intentional: sequential polling with delay between attempts */
  async pollCommandStatus(
    commandId: string,
    signal?: AbortSignal,
  ): Promise<string[] | null> {
    for (let attempt = 0; attempt < pollMaxAttempts; attempt++) {
      if (signal?.aborted) return null;

      try {
        const res = await fetch(
          `${this.baseUrl}/widget/commands/${encodeURIComponent(commandId)}/status`,
          { headers: this.headers, signal },
        );

        if (res.ok) {
          const json = (await res.json()) as ApiResponse<CommandStatusResponse>;
          const { status, data, error } = json.data;

          if (status === "COMPLETED") {
            debugLog(
              `Command completed on attempt ${attempt + 1}/${pollMaxAttempts}`,
            );
            return data?.matchedMarketEventIds ?? [];
          }

          if (status === "FAILED" || status === "REJECTED") {
            errorLog(`Command ${status}: ${error ?? "unknown error"}`);
            return null;
          }
        } else if (
          res.status >= 400 &&
          res.status < 500 &&
          res.status !== 408 &&
          res.status !== 429
        ) {
          // 4xx is a permanent client-side failure (wrong API key, malformed
          // request, missing command id, etc.) — bail immediately instead of
          // burning the polling budget. 408 (Request Timeout) and 429 (Too
          // Many Requests) are the documented retry-worthy 4xx codes.
          errorLog(
            `Command status check returned permanent failure ${res.status}; aborting poll`,
          );
          return null;
        } else {
          debugLog(`Command status check returned ${res.status}, will retry`);
        }
      } catch (err) {
        if (isAbortError(err)) return null;
        errorLog("Command status poll error", err);
      }

      if (attempt < pollMaxAttempts - 1) {
        const delayMs = computePollDelay(attempt);
        debugLog(
          `Command still processing, polling again in ${Math.round(delayMs)}ms (attempt ${attempt + 1}/${pollMaxAttempts})`,
        );
        try {
          await sleep(delayMs, signal);
        } catch {
          return null;
        }
      }
    }

    debugLog("Command status polling timed out");
    return null;
  }
  /* eslint-enable no-await-in-loop -- Re-enable after polling method */

  async getMarketEvent(marketEventId: string): Promise<IMarketEventDTO | null> {
    try {
      const res = await fetch(
        `${this.baseUrl}/widget/markets/event/${encodeURIComponent(marketEventId)}`,
        { headers: this.headers },
      );

      if (!res.ok) {
        errorLog(`Market event request failed with status ${res.status}`);
        return null;
      }

      const json = (await res.json()) as ApiResponse<IMarketEventDTO>;
      return json.data;
    } catch (err) {
      errorLog("Market event request failed", err);
      return null;
    }
  }

  async getFeaturedMarkets(limit = 1): Promise<IMarketEventDTO[] | null> {
    try {
      const url = new URL(`${this.baseUrl}/widget/markets/events/featured`);
      url.searchParams.set("pageSize", String(limit));

      const res = await fetch(url.toString(), { headers: this.headers });

      if (!res.ok) {
        errorLog(`Featured markets request failed with status ${res.status}`);
        return null;
      }

      const json = (await res.json()) as PaginatedApiResponse<IMarketEventDTO>;
      return json.data;
    } catch (err) {
      errorLog("Featured markets request failed", err);
      return null;
    }
  }
}
